import { useState } from 'react'
import type { RiskItemDTO } from '@deepguide-ai/types'
import { useMutation } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'

type AddRiskDialogProps = {
  open: boolean
  onClose: () => void
  onCreated: (risk?: RiskItemDTO) => void
  vendorId?: string
  item?: Pick<RiskItemDTO, 'id' | 'title' | 'riskStatement'>
}

const fieldLabelClassName =
  'font-body mb-1 block text-[11px] font-semibold text-text-subtle'
const fieldInputClassName = 'h-9 rounded-sm border-border-divider text-[12px]'
const fieldTextareaClassName =
  'min-h-[72px] rounded-sm border-border-divider text-[12px]'

export default function AddRiskDialog({
  open,
  onClose,
  onCreated,
  item,
  vendorId,
}: AddRiskDialogProps) {
  const createMutation = useMutation(trpc.riskRegister.create.mutationOptions())
  const updateMutation = useMutation(trpc.riskRegister.update.mutationOptions())
  const mutation = item ? updateMutation : createMutation
  const [formData, setFormData] = useState(() => ({ title: item?.title ?? '', riskStatement: item?.riskStatement ?? '' }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mutation.isPending) return
    const onSuccess = (risk: RiskItemDTO) => {
      onCreated(risk)
      onClose()
    }
    if (item) {
      updateMutation.mutate({ id: item.id, data: formData }, { onSuccess })
    } else {
      createMutation.mutate({ ...formData, ...(vendorId ? { vendorId } : {}) }, { onSuccess })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        className='flex max-h-[85vh] max-w-2xl flex-col overflow-hidden p-0'
        closeClassName='top-4.5'
      >
        <DialogHeader className='shrink-0'>
          <DialogTitle className='font-heading text-[15px] font-semibold text-foreground'>
            {item ? 'Edit Risk' : 'Add Risk'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col'>
          <div className='space-y-4 overflow-y-auto px-6 py-5'>
            <div className='space-y-1.5'>
              <Label htmlFor='risk-title' className={fieldLabelClassName}>Title</Label>
              <Input
                id='risk-title'
                className={fieldInputClassName}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={500}
                required
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='risk-description' className={fieldLabelClassName}>Description</Label>
              <Textarea
                id='risk-description'
                className={fieldTextareaClassName}
                value={formData.riskStatement}
                onChange={(e) => setFormData({ ...formData, riskStatement: e.target.value })}
                required
              />
            </div>
            {!item && <p className='text-[12px] text-muted-foreground'>
              The risk will be created as Open. Click Start assessment when you’re ready to review it.
            </p>}
            {mutation.error && <p role='alert' className='text-xs text-destructive'>{mutation.error.message}</p>}
          </div>
          <div className='flex shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-3'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-8 rounded-sm border-border-divider text-[12px]'
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              size='sm'
              className='h-8 rounded-sm bg-primary text-[12px] text-primary-foreground hover:bg-primary/90'
              disabled={mutation.isPending}
            >
              {item ? mutation.isPending ? 'Saving...' : 'Save changes' : mutation.isPending ? 'Creating...' : 'Create Risk'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
